import { useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Show, useObservable } from "@legendapp/state/react"
import { Link, useLocalSearchParams, useRouter } from "expo-router"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { Pressable, View, ActivityIndicator } from "react-native"

import { z } from "@white-labels/input"

import { ar } from "@src/i18n/ar"
import { useNumCols } from "@src/hooks"
import { dayjs, formatDateForDisplay, sharedStyles, trpc } from "@src/utils"
import PurchaseLineChart from "@src/components/purchase-line-chart"
import { Button, DatePicker, Header, ModalWrapper, ProductPicker, Text, ScrollView } from "@src/components"

const schema = z.object({ storagePlaceId: z.number(), productIds: z.array(z.number()), from: z.number(), to: z.number() })
type Filters = z.infer<typeof schema>
type DatePreset = "today" | "week" | "month" | "year" | "custom"

export default function InventoryMovement() {
  const router = useRouter()
  const params = useLocalSearchParams<{ id: string; productIds?: string; from?: string; to?: string; preset?: DatePreset }>()
  const showFilter$ = useObservable(false)
  const storagePlaceId = Number.parseInt(params.id, 10)

  // Parse filters from URL or use defaults
  const filters = useMemo<Filters>(() => {
    const now = dayjs.utc()
    return {
      storagePlaceId,
      productIds: params.productIds ? params.productIds.split(",").map(Number).filter(Boolean) : [],
      from: params.from ? Number(params.from) : now.startOf("day").subtract(6, "day").unix(),
      to: params.to ? Number(params.to) : now.endOf("day").unix(),
    }
  }, [params.id, params.productIds, params.from, params.to])

  const datePreset: DatePreset = params.preset || "week"

  // Update URL with new filters
  const updateFilters = (newFilters: Partial<Filters>, newPreset?: DatePreset) => {
    const merged = { ...filters, ...newFilters }
    router.setParams({
      productIds: merged.productIds.length > 0 ? merged.productIds.join(",") : undefined,
      from: String(merged.from),
      to: String(merged.to),
      preset: newPreset ?? datePreset,
    })
  }

  // Presets handler
  const applyDatePreset = (preset: DatePreset) => {
    const now = dayjs.utc()
    let from: dayjs.Dayjs
    const to: dayjs.Dayjs = now.endOf("day")

    switch (preset) {
      case "today":
        from = now.subtract(24, "hour") // Last 24 hours
        break
      case "week":
        from = now.subtract(7, "day").startOf("day") // Last 7 days
        break
      case "month":
        from = now.subtract(30, "day").startOf("day") // Last 30 days
        break
      case "year":
        from = now.subtract(365, "day").startOf("day") // Last 365 days
        break
      case "custom":
        router.setParams({ preset: "custom" })
        return
    }

    updateFilters({ from: from.unix(), to: to.unix() }, preset)
  }

  return (
    <>
      <Header
        title={ar.inventoryMovements}
        right={
          <Pressable hitSlop={12} onPress={() => showFilter$.set(true)} className="h-full justify-center">
            <MIcon name="filter-variant" size={24} />
          </Pressable>
        }
      />

      {/* Date Presets UI */}
      <View className="flex-row max-w-content-wide w-full mx-auto my-1 gap-1">
        {(["today", "week", "month", "year", "custom"] as DatePreset[]).map((preset) => (
          <Pressable key={preset} onPress={() => applyDatePreset(preset)} className={`px-3 py-1 rounded-md ${datePreset === preset ? "bg-blue-600" : "bg-white"}`}>
            <Text className={`text-xs ${datePreset === preset ? "text-white" : "text-gray-600"}`}>{ar[preset]}</Text>
          </Pressable>
        ))}
      </View>

      <ProductStats
        filters={filters}
        datePreset={datePreset}
        onRemoveProduct={(id) => {
          const newIds = filters.productIds.filter((pid) => pid !== id)
          updateFilters({ productIds: newIds })
        }}
      />

      <Show if={showFilter$}>
        <InventoryMovementFilterModal
          filters={filters}
          onClose={() => showFilter$.set(false)}
          onSubmit={(newFilter) => {
            updateFilters(newFilter, "custom")
            showFilter$.set(false)
          }}
        />
      </Show>
    </>
  )
}

function ProductStats({ filters, datePreset, onRemoveProduct }: { filters: Filters; datePreset: DatePreset; onRemoveProduct: (id: number) => void }) {
  const hasProducts = filters.productIds.length > 0
  const stats = trpc.inventory.productSales.useQuery(filters, { enabled: hasProducts })
  const numCols = Math.min(3, Math.max(1, useNumCols(300, { maxWidth: 1200 })))

  // Decide granularity: today=hour, week/month=day, year=month
  const granularity: "hour" | "day" | "week" | "month" = datePreset === "today" ? "hour" : datePreset === "year" ? "month" : "day"

  const purchaseStats = trpc.inventory.purchaseFrequency.useQuery(
    {
      storagePlaceId: filters.storagePlaceId,
      productIds: filters.productIds,
      from: filters.from,
      to: filters.to,
      granularity,
    },
    { enabled: hasProducts },
  )

  if (!hasProducts) {
    return (
      <View className="flex-1 justify-center items-center p-8 max-w-content w-full mx-auto">
        <MIcon name="chart-bar" size={48} color="#ddd" />
        <Text className="text-center font-bold mt-4 text-gray-500">{ar.selectProduct}</Text>
      </View>
    )
  }

  if (stats.isLoading) {
    return <ActivityIndicator className="mt-4" />
  }

  const products = stats.data?.products ?? []

  return (
    <ScrollView className="flex-1" contentContainerStyle={sharedStyles.contentContainerWide} onRefresh={() => stats.refetch()}>
      {/* Date Range Display - RTL order (to ← from) */}
      <View className="flex-row justify-between mb-4 bg-white rounded-lg p-3 border border-gray-100">
        <Text className="text-sm font-bold text-gray-500">{formatDateForDisplay(filters.to, "D MMM")}</Text>
        <MIcon name="arrow-left" size={16} color="#9CA3AF" />
        <Text className="text-sm font-bold text-gray-500">{formatDateForDisplay(filters.from, "D MMM")}</Text>
      </View>

      {/* Sales Trend Line Chart - Above Products */}
      {purchaseStats.data && purchaseStats.data.length > 0 && (
        <View className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 mb-4">
          <Text className="text-gray-400 text-xs mb-3 font-bold">{ar.purchaseFrequency}</Text>
          <View style={{ aspectRatio: 1, maxHeight: 300 }}>
            <PurchaseLineChart products={purchaseStats.data} dom={{ matchContents: true }} />
          </View>
        </View>
      )}

      {/* Per-Product Stats Cards - Responsive Grid */}
      <View className="flex-row flex-wrap" style={{ marginHorizontal: -4 }}>
        {products.map((product) => (
          <View key={product.id} style={{ width: `${100 / numCols}%`, padding: 4 }}>
            <View className="bg-white rounded-lg border border-gray-100 shadow-sm p-4">
              <View className="flex-row justify-between items-start mb-3">
                <Link href={`/dashboard/inventory/storage/${filters.storagePlaceId}/product/${product.id}`} asChild>
                  <Pressable className="flex-1">
                    <Text className="font-bold text-gray-800" numberOfLines={1}>
                      {product.name}
                    </Text>
                    <Text className="text-xs text-gray-400">{product.upc}</Text>
                  </Pressable>
                </Link>
                <Pressable onPress={() => onRemoveProduct(product.id)} hitSlop={8} className="bg-gray-100 rounded-full p-1 ml-2">
                  <MIcon name="close" size={14} color="#666" />
                </Pressable>
              </View>

              <View className="flex-row gap-3">
                {/* Inventory */}
                <View className="flex-1 bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <Text className="text-[10px] text-blue-600 mb-1">{ar.currentInventory}</Text>
                  {product.isWeightProduct ? (
                    <View className="flex-row items-baseline">
                      <Text className="text-lg font-bold text-blue-900">{(product.inventory.quantity / 1000).toFixed(2)}</Text>
                      <Text className="text-[10px] text-blue-600 ml-1">{ar.kg}</Text>
                    </View>
                  ) : (
                    <Text className="text-lg font-bold text-blue-900">{product.inventory.quantity}</Text>
                  )}
                </View>

                {/* Sales */}
                <View className="flex-1 bg-green-50 p-3 rounded-lg border border-green-100">
                  <Text className="text-[10px] text-green-600 mb-1">{ar.totalSales}</Text>
                  {product.isWeightProduct ? (
                    <View className="flex-row items-baseline">
                      <Text className="text-lg font-bold text-green-700">{(product.sales.grams / 1000).toFixed(2)}</Text>
                      <Text className="text-[10px] text-green-600 ml-1">{ar.kg}</Text>
                    </View>
                  ) : (
                    <Text className="text-lg font-bold text-green-700">{product.sales.quantity}</Text>
                  )}
                </View>
              </View>
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  )
}

function InventoryMovementFilterModal({ onClose, onSubmit, filters }: { onClose(): void; onSubmit(values: Filters): void; filters: Filters }) {
  const { control, handleSubmit, setValue, watch } = useForm<Filters>({
    resolver: zodResolver(schema),
    defaultValues: filters,
  })

  // We are using `productIds` now (array)
  const selectedProductIds = watch("productIds")

  return (
    <ModalWrapper onClose={onClose}>
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-xl font-bold">{ar.filter}</Text>
        <Pressable onPress={onClose}>
          <MIcon name="close" size={24} color="#666" />
        </Pressable>
      </View>

      {/* Date Range */}
      <View className="flex-row gap-1 mb-4">
        <View className="flex-1">
          <Text className="mb-1 text-gray-600 text-xs">{ar.from}</Text>
          <Controller
            control={control}
            name="from"
            render={({ field: { onChange, value } }) => (
              <DatePicker mode="datetime" value={value ? dayjs.unix(value).toDate() : undefined} onChange={(d) => d && onChange(dayjs(d).unix())} />
            )}
          />
        </View>
        <View className="flex-1">
          <Text className="mb-1 text-gray-600 text-xs">{ar.to}</Text>
          <Controller
            control={control}
            name="to"
            render={({ field: { onChange, value } }) => (
              <DatePicker mode="datetime" value={value ? dayjs.unix(value).toDate() : undefined} onChange={(d) => d && onChange(dayjs(d).unix())} />
            )}
          />
        </View>
      </View>

      <View className="gap-1 mb-4">
        <Text className="text-gray-600 text-xs">{ar.products}</Text>
        {/* Pass storagePlaceId for filtering if ProductPicker supports it (it should, based on recent changes or existing capability). 
            The existing ProductPicker does NOT have a `filters` prop in the code I read earlier.
            Wait, I read `components/product-picker.tsx` earlier. 
            It accepts: value, onChange, hide, mode. 
            It uses `trpc.products.list.useInfiniteQuery`.
            `packages/input/src/m5azen.ts` defines `listProductsInput` which does NOT have `storagePlaceId`.
            BUT `listProductsByStoragePlaceInput` HAS `storagePlaceId`.
            The ProductPicker uses `trpc.products.list` which likely maps to `listProductsInput`.
            So ProductPicker currently lists ALL products. 
            The USER request said: 
            `- <ProductPicker filters={{ storagePlaceId: filters.storagePlaceId }} />`
            But I didn't verify if ProductPicker supports filters. 
            Checking `product-picker.tsx`:
            `const products = trpc.products.list.useInfiniteQuery({ search: debouncedSearch, limit: 50 }, ...)`
            It does NOT accept extra filters.
            
            ISSUE: ProductPicker needs to support filtering by storagePlaceId if we want to restrict selection to current storage place.
            However, user didn't explicitly ask to modify ProductPicker interna to support storagePlaceId filtering, 
            BUT in the plan step 3.2: `filters={{ storagePlaceId: filters.storagePlaceId }}` was suggested.
            
            This implies I should ALSO update ProductPicker to support filters or at least storagePlaceId.
            OR I just proceed without it for now (user usually refactors step by step).
            BUT strict adherence to plan 3.2 suggests I should add it.
            
            Wait, looking at `product-picker.tsx` again (Step 15):
            It does NOT seem to look for `filters` prop.
            
            I will use the `ProductPicker` as is for now regarding `filters` prop (ignoring it effectively or check if I can add it quickly).
            Adding it would require updating the trpc router for `products.list` to accept `storagePlaceId` or using `products.listByStoragePlace`.
            
            The current `products.list` input is `listProductsInput`.
            Checking `packages/input/src/m5azen.ts`: `listProductsInput` does NOT have storagePlaceId.
            `listProductsByStoragePlaceInput` DOES.
            
            So `products.list` likely doesn't support generic filtering by storage place easily unless I change it.
            
            For now, I will NOT pass `filters` to ProductPicker to avoid scope creep (modifying ProductPicker and Product Router wasn't explicitly broken out as a task, just "Update ProductPicker usage").
            I will use `mode="multiple"`.
        */}
        {/* TODO: FIX ProductPicker */}
        <ProductPicker
          value={selectedProductIds}
          // movement .. WEIGHT_PRODUCT and not WEIGHT_DEFINED_PRODUCT ..
          types={["LOCAL_PRODUCT", "STANDARD_PRODUCT", "WEIGHT_PRODUCT"]}
          onChange={(ids) => {
            setValue("productIds", ids)
          }}
          mode="multiple"
        />
      </View>

      {/* Show selected count + limit warning */}
      {selectedProductIds.length > 0 && (
        <View className="mb-4">
          <Text className={`text-xs ${selectedProductIds.length >= 10 ? "text-red-500 font-bold" : "text-gray-500"}`}>
            {selectedProductIds.length}/10 {ar.products}
            {selectedProductIds.length >= 10 && ` (${ar.maxProductsReached})`}
          </Text>
        </View>
      )}

      <Button onPress={handleSubmit(onSubmit)} className="mt-2">
        {ar.apply}
      </Button>
    </ModalWrapper>
  )
}
